import React, { useState, useRef, useEffect } from 'react';
import { Tool, Shape } from '../types/Shape';
import { useI18n } from '../contexts/I18nContext';
import { Language, languageNames } from '../i18n';

interface ToolbarProps {
  currentTool: Tool;
  onToolChange: (tool: Tool) => void;
  onSave: () => void;
  onLoad: (file: File) => void;
  onExport?: () => void;
  onImageConcat?: () => void;
  defaultStartArrow: boolean;
  defaultEndArrow: boolean;
  defaultIsCurved: boolean;
  onDefaultStartArrowChange: (value: boolean) => void;
  onDefaultEndArrowChange: (value: boolean) => void;
  onDefaultIsCurvedChange: (value: boolean) => void;
  defaultArrowStroke: string;
  defaultArrowStrokeWidth: number;
  onDefaultArrowStrokeChange: (value: string) => void;
  onDefaultArrowStrokeWidthChange: (value: number) => void;
  defaultFill: string;
  defaultStroke: string;
  defaultStrokeWidth: number;
  defaultFontSize: number;
  onDefaultFillChange: (value: string) => void;
  onDefaultStrokeChange: (value: string) => void;
  onDefaultStrokeWidthChange: (value: number) => void;
  onDefaultFontSizeChange: (value: number) => void;
  selectedShapes: Shape[];
  onUpdateShapes: (updates: Partial<Shape>) => void;
  children?: React.ReactNode;
}

const Toolbar: React.FC<ToolbarProps> = ({
  currentTool,
  onToolChange,
  onSave,
  onLoad,
  onExport,
  onImageConcat,
  defaultStartArrow,
  defaultEndArrow,
  defaultIsCurved,
  onDefaultStartArrowChange,
  onDefaultEndArrowChange,
  onDefaultIsCurvedChange,
  defaultArrowStroke,
  defaultArrowStrokeWidth,
  onDefaultArrowStrokeChange,
  onDefaultArrowStrokeWidthChange,
  defaultFill,
  defaultStroke,
  defaultStrokeWidth,
  defaultFontSize,
  onDefaultFillChange,
  onDefaultStrokeChange,
  onDefaultStrokeWidthChange,
  onDefaultFontSizeChange,
  selectedShapes,
  onUpdateShapes,
  children
}) => {
  // i18n
  const { language, setLanguage, t } = useI18n();

  // 선택된 객체 중 첫 번째 것을 기본값으로 사용
  const firstShape = selectedShapes[0];
  const hasArrow = selectedShapes.length === 1 && firstShape?.type === 'arrow';
  const hasNonArrow = selectedShapes.length > 0 && selectedShapes.some(s => s.type !== 'arrow');
  const hasShapeToolSelected = currentTool === 'rect' || currentTool === 'circle' || currentTool === 'code' || currentTool === 'md';
  return (
    <div className="toolbar-wrapper">
      {/* 첫 번째 줄: 도구 버튼 */}
      <div className="toolbar-main">
        <button
          className="toolbar-btn"
          onClick={onSave}
          title={t.tooltips.save}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 4v12m-4-4l4 4 4-4" />
          </svg>
          <span className="toolbar-label">{t.toolbar.save}</span>
        </button>
        <input
          type="file"
          accept=".json"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              onLoad(file);
              e.target.value = '';
            }
          }}
          style={{ display: 'none' }}
          id="load-file-input"
        />
        <button
          className="toolbar-btn"
          onClick={() => document.getElementById('load-file-input')?.click()}
          title={t.tooltips.load}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          <span className="toolbar-label">{t.toolbar.load}</span>
        </button>
        {onExport && (
          <button
            className="toolbar-btn"
            onClick={onExport}
            title={t.tooltips.export}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <path d="M10 12l3-3-3-3M7 9h6" />
            </svg>
            <span className="toolbar-label">{t.toolbar.export}</span>
          </button>
        )}
        <div className="toolbar-divider"></div>

        <button
          className={`toolbar-btn ${currentTool === 'select' ? 'active' : ''}`}
          onClick={() => onToolChange('select')}
          title={t.tooltips.select}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 4 Q 6 2, 9 3 Q 12 4, 14 7 Q 16 10, 15 13 Q 14 16, 11 16 Q 8 16, 6 14 Q 4 12, 3 9 Q 2 6, 3 4" strokeDasharray="2 2" />
          </svg>
          <span className="toolbar-label">{t.toolbar.select}</span>
        </button>
        <button
          className={`toolbar-btn ${currentTool === 'rect' ? 'active' : ''}`}
          onClick={() => onToolChange('rect')}
          title={t.tooltips.rect}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="4" y="4" width="12" height="12" />
          </svg>
          <span className="toolbar-label">{t.toolbar.rect}</span>
        </button>
        <button
          className={`toolbar-btn ${currentTool === 'circle' ? 'active' : ''}`}
          onClick={() => onToolChange('circle')}
          title={t.tooltips.circle}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="10" cy="10" r="6" />
          </svg>
          <span className="toolbar-label">{t.toolbar.circle}</span>
        </button>
        <button
          className={`toolbar-btn ${currentTool === 'arrow' ? 'active' : ''}`}
          onClick={() => onToolChange('arrow')}
          title={t.tooltips.arrow}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 10h12M12 6l4 4-4 4" />
          </svg>
          <span className="toolbar-label">{t.toolbar.arrow}</span>
        </button>
        <button
          className={`toolbar-btn ${currentTool === 'code' ? 'active' : ''}`}
          onClick={() => onToolChange('code')}
          title={t.tooltips.code}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 6l-3 4 3 4M13 6l3 4-3 4" />
          </svg>
          <span className="toolbar-label">{t.toolbar.code}</span>
        </button>
        <button
          className={`toolbar-btn ${currentTool === 'md' ? 'active' : ''}`}
          onClick={() => onToolChange('md')}
          title={t.tooltips.markdown}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 2h2M12 2h2M4 5h12M4 8h8M4 11h12M4 14h8M4 17h12" />
          </svg>
          <span className="toolbar-label">{t.toolbar.markdown}</span>
        </button>
        {onImageConcat && (
          <button
            className="toolbar-btn"
            onClick={onImageConcat}
            title={t.tooltips.imageConcat}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="4" y="2" width="12" height="5" rx="1" />
              <rect x="4" y="13" width="12" height="5" rx="1" />
              <path d="M10 7v6" strokeDasharray="2 2" />
            </svg>
            <span className="toolbar-label">{t.toolbar.image}</span>
          </button>
        )}
        {children}

        <div className="toolbar-divider"></div>

        {/* 언어 선택 */}
        <select
          className="language-select"
          value={language}
          onChange={(e) => setLanguage(e.target.value as Language)}
          title={t.settings.language}
        >
          <option value="en">{languageNames.en}</option>
          <option value="ko">{languageNames.ko}</option>
        </select>
      </div>



      {/* 두 번째 줄: 도구별 속성 */}
      {/* 화살표 도구 기본 설정 */}
      {currentTool === 'arrow' && !hasArrow && !hasNonArrow && (
        <div className="toolbar-properties">
          <label className="property-item">
            <input
              type="checkbox"
              checked={defaultStartArrow}
              onChange={(e) => onDefaultStartArrowChange(e.target.checked)}
            />
            <span>{t.properties.startPoint}</span>
          </label>
          <label className="property-item">
            <input
              type="checkbox"
              checked={defaultEndArrow}
              onChange={(e) => onDefaultEndArrowChange(e.target.checked)}
            />
            <span>{t.properties.endPoint}</span>
          </label>
          <label className="property-item">
            <input
              type="checkbox"
              checked={defaultIsCurved}
              onChange={(e) => onDefaultIsCurvedChange(e.target.checked)}
            />
            <span>{t.properties.curved}</span>
          </label>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.color}</span>
            <input
              type="color"
              value={defaultArrowStroke}
              onChange={(e) => onDefaultArrowStrokeChange(e.target.value)}
              className="color-input-small"
            />
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.thickness}</span>
            <input
              type="range"
              min="1"
              max="10"
              step="0.5"
              value={defaultArrowStrokeWidth}
              onChange={(e) => onDefaultArrowStrokeWidthChange(parseFloat(e.target.value))}
              className="slider-input-small"
            />
            <span className="slider-value-small">{defaultArrowStrokeWidth}</span>
          </div>
        </div>
      )}

      {/* 사각형/원/코드 도구 기본 설정 */}
      {hasShapeToolSelected && !hasNonArrow && (
        <div className="toolbar-properties">
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.background}</span>
            <input
              type="color"
              value={defaultFill}
              onChange={(e) => onDefaultFillChange(e.target.value)}
              className="color-input-small"
            />
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.border}</span>
            <input
              type="color"
              value={defaultStroke}
              onChange={(e) => onDefaultStrokeChange(e.target.value)}
              className="color-input-small"
            />
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.thickness}</span>
            <input
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={defaultStrokeWidth}
              onChange={(e) => onDefaultStrokeWidthChange(parseFloat(e.target.value))}
              className="slider-input-small"
            />
            <span className="slider-value-small">{defaultStrokeWidth}</span>
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.font}</span>
            <select
              value={defaultFontSize}
              onChange={(e) => onDefaultFontSizeChange(parseInt(e.target.value))}
              className="select-input-small"
            >
              <option value="12">12</option>
              <option value="14">14</option>
              <option value="16">16</option>
              <option value="18">18</option>
              <option value="20">20</option>
              <option value="24">24</option>
              <option value="28">28</option>
              <option value="32">32</option>
              <option value="36">36</option>
            </select>
          </div>
        </div>
      )}

      {/* 화살표 선택 시 속성 */}
      {hasArrow && (
        <div className="toolbar-properties">
          <label className="property-item">
            <input
              type="checkbox"
              checked={firstShape.startArrow ?? false}
              onChange={(e) => onUpdateShapes({ startArrow: e.target.checked })}
            />
            <span>{t.properties.startPoint}</span>
          </label>
          <label className="property-item">
            <input
              type="checkbox"
              checked={firstShape.endArrow ?? true}
              onChange={(e) => onUpdateShapes({ endArrow: e.target.checked })}
            />
            <span>{t.properties.endPoint}</span>
          </label>
          <label className="property-item">
            <input
              type="checkbox"
              checked={firstShape.isCurved ?? false}
              onChange={(e) => onUpdateShapes({ isCurved: e.target.checked })}
            />
            <span>{t.properties.curved}</span>
          </label>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.color}</span>
            <input
              type="color"
              value={firstShape.stroke || '#1E40AF'}
              onChange={(e) => onUpdateShapes({ stroke: e.target.value })}
              className="color-input-small"
            />
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.thickness}</span>
            <input
              type="range"
              min="1"
              max="10"
              step="0.5"
              value={firstShape.strokeWidth || 2}
              onChange={(e) => onUpdateShapes({ strokeWidth: parseFloat(e.target.value) })}
              className="slider-input-small"
            />
            <span className="slider-value-small">{firstShape.strokeWidth || 2}</span>
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.priority}</span>
            <input
              type="number"
              value={firstShape.order || 0}
              onChange={(e) => onUpdateShapes({ order: parseInt(e.target.value) || 0 })}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  e.stopPropagation();
                }
              }}
              className="number-input-small"
              style={{ width: '60px', padding: '2px 4px', fontSize: '11px' }}
            />
          </div>
        </div>
      )}

      {/* 일반 객체 선택 시 속성 */}
      {hasNonArrow && (
        <div className="toolbar-properties">
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.background}</span>
            <input
              type="color"
              value={firstShape?.fill || '#FFFFFF'}
              onChange={(e) => onUpdateShapes({ fill: e.target.value })}
              className="color-input-small"
            />
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.border}</span>
            <input
              type="color"
              value={firstShape?.stroke || '#D1D5DB'}
              onChange={(e) => onUpdateShapes({ stroke: e.target.value })}
              className="color-input-small"
            />
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.thickness}</span>
            <input
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={firstShape?.strokeWidth || 1}
              onChange={(e) => onUpdateShapes({ strokeWidth: parseFloat(e.target.value) })}
              className="slider-input-small"
            />
            <span className="slider-value-small">{firstShape?.strokeWidth || 1}</span>
          </div>
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.font}</span>
            <select
              value={firstShape?.fontSize || 16}
              onChange={(e) => onUpdateShapes({ fontSize: parseInt(e.target.value) })}
              className="select-input-small"
            >
              <option value="12">12</option>
              <option value="14">14</option>
              <option value="16">16</option>
              <option value="18">18</option>
              <option value="20">20</option>
              <option value="24">24</option>
              <option value="28">28</option>
              <option value="32">32</option>
              <option value="36">36</option>
            </select>
          </div>
          {selectedShapes.length > 1 && (
            <span className="selection-count-small">({selectedShapes.filter(s => s.type !== 'arrow').length}{t.properties.items})</span>
          )}
          <div className="property-item-inline">
            <span className="property-label-inline">{t.properties.priority}</span>
            <input
              type="number"
              value={firstShape?.order || 0}
              onChange={(e) => onUpdateShapes({ order: parseInt(e.target.value) || 0 })}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  e.stopPropagation();
                }
              }}
              className="number-input-small"
              style={{ width: '60px', padding: '2px 4px', fontSize: '11px' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Toolbar;
